import React from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Stack,
    Switch,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import { CustomSentenceApi, ICustomSentence } from '../api/CustomSentenceApi';
import { AudioApi } from '../api/AudioApi';
import { ISentenceScore } from '../api/ScoreApi';
import { ScoreGrade, sentenceScoreStatusMessage } from '../Score';
import { useAudioRecorder } from '../AudioRecorder';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { TeacherAudioReportDialog, getSelectedTeacherSpeakerRole } from './TeacherAudioReportButton';
import {
    BLUE,
    BORDER,
    ERROR,
    ERROR_TEXT,
    GREEN,
    INTER,
    NAVY,
    POPPINS,
    PROGRESS_TRACK,
    SLATE,
    SURFACE_MUTED,
    YELLOW,
} from '../style/verbaScholaPalette';
import { pronunciationButtonSx, pronunciationError } from './studentPronunciationStyles';
import { StudentRecordingStatus } from './StudentSpeakingControls';
import { instructionalPinyin } from './studentInstructionalPinyin';

// Practice takes stop on their own after five minutes; the recording status shows this limit.
const PRONUNCIATION_MAX_SECONDS = 300;

const gradeColors: Record<ScoreGrade, string> = { good: GREEN, ok: YELLOW, bad: ERROR };
const gradeLabels: Record<ScoreGrade, string> = { good: 'Good', ok: 'Close', bad: 'Needs practice' };
const parts = ['initial', 'final', 'tone'] as const;
const isHanzi = (character: string) => /\p{Script=Han}/u.test(character);
const numeric = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const when = (score: ISentenceScore, index = 0) => (score.created_at ? new Date(score.created_at).toLocaleString() : `Recording ${index + 1}`);

function ScoreDetails({ score }: { score: ISentenceScore }) {
    const rows = score.character_scores?.length
        ? score.character_scores
        : Array.from(score.text || '').map((character, index) => ({
              order_index: index,
              character,
              expected_pinyin: score.pinyin_list_transcript?.[index],
              spoken_pinyin: score.pinyin_list_spoken?.[index],
              score: score.score?.[index],
              score_grade: score.score_grade?.[index],
              is_silence: score.is_silence?.[index],
              initial_grade: null,
              initial_score: null,
              final_grade: null,
              final_score: null,
              tone_grade: null,
              tone_score: null,
          }));
    const hasComponents = rows.some((row) => parts.some((part) => row[`${part}_grade`] != null || row[`${part}_score`] != null));
    return (
        <Box sx={{ mt: 2.25 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {rows
                    .filter((row) => !(row.character === '-' && row.is_silence))
                    .map((row) => {
                        const grade = row.score_grade;
                        const target = row.expected_pinyin ? instructionalPinyin([row.expected_pinyin]) : '—';
                        const heard = row.is_silence ? 'Not heard' : row.spoken_pinyin ? instructionalPinyin([row.spoken_pinyin]) : '—';
                        return (
                            <Box
                                key={row.order_index}
                                sx={{
                                    minWidth: 56,
                                    p: 1,
                                    borderRadius: '12px',
                                    backgroundColor: SURFACE_MUTED,
                                    textAlign: 'center',
                                    border: row.character === '-' || row.is_silence ? `1px dashed ${SLATE}` : undefined,
                                }}
                            >
                                <Typography
                                    sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: row.character === '-' ? 11 : 20, color: NAVY }}
                                >
                                    {row.character === '-' ? 'Extra sound' : row.character || '—'}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: SLATE }}>{target}</Typography>
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        color: target === heard ? SLATE : ERROR_TEXT,
                                        fontWeight: target === heard ? 400 : 700,
                                    }}
                                >
                                    {heard}
                                </Typography>
                                {hasComponents ? (
                                    <Stack direction="row" spacing={0.4} sx={{ mt: 0.75 }}>
                                        {parts.map((part) => {
                                            const detail = { grade: row[`${part}_grade`], score: row[`${part}_score`] };
                                            const label = `${part}: ${detail?.grade ? gradeLabels[detail.grade] : 'Not graded'}${numeric(detail?.score) ? `, ${Math.round(detail.score)} out of 100` : ''}`;
                                            return (
                                                <Tooltip key={part} title={label}>
                                                    <Box
                                                        tabIndex={0}
                                                        role="img"
                                                        aria-label={label}
                                                        sx={{
                                                            flex: 1,
                                                            minWidth: 12,
                                                            height: 7,
                                                            borderRadius: '999px',
                                                            backgroundColor: detail?.grade ? gradeColors[detail.grade] : BORDER,
                                                        }}
                                                    />
                                                </Tooltip>
                                            );
                                        })}
                                    </Stack>
                                ) : (
                                    <Tooltip title={grade ? gradeLabels[grade] : 'Not graded'}>
                                        <Box
                                            tabIndex={0}
                                            role="img"
                                            aria-label={`Character grade: ${grade ? gradeLabels[grade] : 'Not graded'}`}
                                            sx={{
                                                mt: 0.75,
                                                height: 7,
                                                borderRadius: '999px',
                                                backgroundColor: grade ? gradeColors[grade] : BORDER,
                                            }}
                                        />
                                    </Tooltip>
                                )}
                                {numeric(row.score) && (
                                    <Typography sx={{ fontSize: 11, color: SLATE, mt: 0.5 }}>{Math.round(row.score)}/100</Typography>
                                )}
                            </Box>
                        );
                    })}
            </Box>
            {hasComponents && (
                <Box component="details" sx={{ mt: 2, color: NAVY, fontSize: 12 }}>
                    <Box component="summary" sx={{ cursor: 'pointer' }}>
                        Sound details
                    </Box>
                    {rows
                        .filter((row) => !(row.character === '-' && row.is_silence))
                        .map((row) => (
                            <Typography key={row.order_index} sx={{ mt: 1, fontSize: 12 }}>
                                {row.character === '-' ? 'Extra sound' : row.character || 'Unaligned sound'}:{' '}
                                {parts
                                    .map((part) => {
                                        const grade = row[`${part}_grade`];
                                        const value = row[`${part}_score`];
                                        return `${part}: ${grade ? gradeLabels[grade] : 'Not graded'}${numeric(value) ? ` (${Math.round(value)}/100)` : ''}`;
                                    })
                                    .join('; ')}
                            </Typography>
                        ))}
                </Box>
            )}
            <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                <Typography sx={{ fontSize: 11, color: SLATE }}>Pinyin: target · heard</Typography>
                <Typography sx={{ fontSize: 11, color: SLATE }}>
                    {hasComponents ? 'Bars: Initial · Final · Tone' : 'Bar: Character grade'}
                </Typography>
                {Object.values(ScoreGrade).map((grade) => (
                    <Stack key={grade} direction="row" spacing={0.5} alignItems="center">
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: gradeColors[grade] }} />
                        <Typography sx={{ fontSize: 11, color: SLATE }}>{gradeLabels[grade]}</Typography>
                    </Stack>
                ))}
            </Stack>
            {!hasComponents && (
                <Typography sx={{ fontSize: 11, color: SLATE, textAlign: 'center', mt: 1 }}>
                    Component details are not available for this recording.
                </Typography>
            )}
            <Typography sx={{ fontSize: 12, color: SLATE, textAlign: 'center', mt: 1.5 }}>
                Listen to the model, then compare it with your recording.
            </Typography>
        </Box>
    );
}

export default function StudentPronunciationPractice({
    sentence,
    onBusy,
    onSummary,
    onSaved,
}: {
    sentence: ICustomSentence;
    onBusy: (busy: boolean) => void;
    onSummary: (id: number, count: number, score: number | null) => void;
    onSaved: () => Promise<void>;
}) {
    const [showPinyin, setShowPinyin] = React.useState(true);
    const [tab, setTab] = React.useState<'score' | 'recordings'>('score');
    const [recordings, setRecordings] = React.useState<ISentenceScore[]>([]);
    const [selectedId, setSelectedId] = React.useState<number | null>(null);
    const [checked, setChecked] = React.useState<number[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState('');
    const [error, setError] = React.useState('');
    const [scoring, setScoring] = React.useState(false);
    const [starting, setStarting] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const [reportOpen, setReportOpen] = React.useState(false);
    const [pendingAudio, setPendingAudio] = React.useState<Blob | null>(null);
    const alive = React.useRef(false);
    const uploadActive = React.useRef(false);
    const playVersion = React.useRef(0);
    const { playAudio, stopAudio } = useAudioPlayer();
    const selected = recordings.find((record) => record.id === selectedId) || recordings[0];

    React.useEffect(() => {
        alive.current = true;
        return () => {
            alive.current = false;
            onBusy(false);
            stopAudio();
        };
    }, [onBusy, stopAudio]);

    const fetchRecordings = React.useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const response = await CustomSentenceApi.getSentenceScore(sentence.id);
            if (!alive.current) return;
            const list = [...response.data].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '') || b.id - a.id);
            setRecordings(list);
            setSelectedId(list[0]?.id ?? null);
        } catch (err) {
            if (alive.current) setLoadError(pronunciationError(err));
        } finally {
            if (alive.current) setLoading(false);
        }
    }, [sentence.id]);

    React.useEffect(() => {
        void fetchRecordings();
    }, [fetchRecordings]);
    React.useEffect(() => {
        if (!loading && !loadError) {
            const latest = recordings[0];
            onSummary(
                sentence.id,
                recordings.length,
                latest && !sentenceScoreStatusMessage(latest) && numeric(latest.score_mean) ? latest.score_mean : null,
            );
        }
    }, [recordings, loading, loadError, onSummary, sentence.id]);

    const submitAudio = async (blob: Blob) => {
        if (!alive.current || uploadActive.current) return;
        uploadActive.current = true;
        setPendingAudio(blob);
        setScoring(true);
        setTab('score');
        setError('');
        try {
            const data = new FormData();
            data.append('audio', blob);
            const response = await CustomSentenceApi.scoreCustomSentence(sentence.id, data, 360_000);
            if (!alive.current) return;
            const result = { ...response.data, created_at: response.data.created_at || new Date().toISOString() };
            setRecordings((current) => [result, ...current.filter((record) => record.id !== result.id)]);
            setSelectedId(result.id);
            setPendingAudio(null);
            void onSaved();
        } catch (err) {
            if (alive.current) {
                const status = (err as { response?: { status?: number } })?.response?.status;
                setError(
                    status === 401
                        ? 'Your sign-in session is no longer valid. Please sign out and sign back in. This recording was not submitted for grading.'
                        : `${pronunciationError(err)} Grading may still finish on the server. Check saved recordings before making another attempt.`,
                );
            }
        } finally {
            uploadActive.current = false;
            if (alive.current) setScoring(false);
        }
    };
    const { startRecording, stopRecording, isRecording } = useAudioRecorder({
        setErrorMessage: setError,
        onStop: (blob) => {
            if (alive.current) void submitAudio(blob);
        },
    });
    const busy = starting || isRecording || scoring || deleting;
    React.useEffect(() => {
        onBusy(busy);
    }, [busy, onBusy]);
    React.useEffect(() => {
        if (!isRecording) return;
        const timer = window.setTimeout(stopRecording, PRONUNCIATION_MAX_SECONDS * 1000);
        return () => window.clearTimeout(timer);
    }, [isRecording, stopRecording]);
    const [recordingSeconds, setRecordingSeconds] = React.useState(0);
    React.useEffect(() => {
        setRecordingSeconds(0);
        if (!isRecording) return;
        const started = Date.now();
        const interval = window.setInterval(() => setRecordingSeconds(Math.floor((Date.now() - started) / 1000)), 250);
        return () => window.clearInterval(interval);
    }, [isRecording]);

    const record = async () => {
        if (isRecording) {
            stopRecording();
            return;
        }
        setStarting(true);
        setError('');
        playVersion.current += 1;
        stopAudio();
        setPendingAudio(null);
        await startRecording();
        if (alive.current) setStarting(false);
    };
    const play = async (score?: ISentenceScore) => {
        const version = ++playVersion.current;
        try {
            const response = score ? await AudioApi.getSentenceScoreAudio(score.id) : await AudioApi.getTeacherSentence(sentence.id);
            if (alive.current && version === playVersion.current)
                playAudio(response.data, {
                    onError: () => {
                        if (alive.current) setError('Unable to play this audio. Please try again.');
                    },
                });
        } catch (err) {
            if (alive.current && version === playVersion.current) setError(pronunciationError(err));
        }
    };
    const deleteRecordings = async () => {
        setDeleting(true);
        setError('');
        const results = await Promise.allSettled(checked.map((id) => CustomSentenceApi.deleteSentenceScore(sentence.id, id)));
        if (!alive.current) return;
        const removed = checked.filter((_id, index) => results[index].status === 'fulfilled');
        setRecordings((current) => current.filter((record) => !removed.includes(record.id)));
        setChecked((current) => current.filter((id) => !removed.includes(id)));
        const failure = results.find((result) => result.status === 'rejected');
        if (failure?.status === 'rejected') setError(pronunciationError(failure.reason));
        setDeleting(false);
        void onSaved();
    };

    let pinyinIndex = 0;
    const statusMessage = selected ? sentenceScoreStatusMessage(selected) : null;
    const scoreNumber = selected && numeric(selected.score_mean) ? Math.round(selected.score_mean) : null;
    return (
        <>
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                <FormControlLabel
                    control={<Switch checked={showPinyin} onChange={(event) => setShowPinyin(event.target.checked)} size="small" />}
                    label="Show pinyin"
                    sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: 12.5, color: SLATE } }}
                />
            </Stack>
            <Box sx={{ py: 3.5, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', rowGap: 1 }}>
                    {Array.from(sentence.text).map((character, index) => {
                        const chinese = isHanzi(character);
                        const pinyin = chinese ? sentence.pinyin[pinyinIndex++] : '';
                        return (
                            <Box key={index} sx={{ minWidth: chinese ? 42 : 15, textAlign: 'center' }}>
                                <Typography
                                    sx={{
                                        fontFamily: POPPINS,
                                        fontWeight: 600,
                                        fontSize: { xs: 27, lg: 34 },
                                        color: NAVY,
                                        lineHeight: 1.25,
                                    }}
                                >
                                    {character}
                                </Typography>
                                {showPinyin && (
                                    <Typography sx={{ minHeight: 22, fontWeight: 600, fontSize: 12, color: BLUE }}>{pinyin ? instructionalPinyin([pinyin]) : ''}</Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
                <Stack direction="row" justifyContent="center" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 1.5 }}>
                    <Button
                        disabled={busy}
                        onClick={() => void play()}
                        startIcon={<VolumeUpRoundedIcon />}
                        sx={{ color: BLUE, fontWeight: 700, fontSize: 12.5, textTransform: 'none' }}
                    >
                        Play model audio
                    </Button>
                    <Button
                        disabled={busy}
                        onClick={() => setReportOpen(true)}
                        startIcon={<ErrorOutlineRoundedIcon />}
                        sx={{ color: SLATE, fontWeight: 600, fontSize: 12, textTransform: 'none' }}
                    >
                        Report audio problem
                    </Button>
                </Stack>
            </Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {isRecording && (
                <Box sx={{ mb: 2.5 }}>
                    <StudentRecordingStatus elapsed={recordingSeconds} maxSeconds={PRONUNCIATION_MAX_SECONDS} label="Recording… speak the whole sentence" />
                </Box>
            )}
            <Button
                fullWidth
                variant="contained"
                disabled={starting || scoring || deleting || loading}
                startIcon={isRecording ? <StopRoundedIcon /> : <MicRoundedIcon />}
                onClick={() => void record()}
                sx={{
                    ...pronunciationButtonSx,
                    py: 1.35,
                    ...(isRecording ? { backgroundColor: ERROR_TEXT, color: '#fff', '&:hover': { backgroundColor: ERROR_TEXT } } : {}),
                }}
            >
                {isRecording
                    ? 'Stop recording'
                    : starting
                      ? 'Opening microphone…'
                      : scoring
                        ? 'Grading recording…'
                        : recordings.length
                          ? 'Record again'
                          : 'Start recording'}
            </Button>
            <Typography sx={{ mt: 1, textAlign: 'center', fontSize: 11.5, color: SLATE }}>
                For the best result, record in a quiet room.
            </Typography>
            {pendingAudio && !scoring && (
                <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 1 }}>
                    <Button disabled={busy} onClick={() => playAudio(pendingAudio)}>
                        Play captured recording
                    </Button>
                    <Button
                        disabled={busy || loading}
                        onClick={() => {
                            void fetchRecordings();
                            void onSaved();
                        }}
                    >
                        Check saved recordings
                    </Button>
                </Stack>
            )}
            <Tabs
                value={tab}
                onChange={(_event, value) => setTab(value)}
                aria-label="Pronunciation results"
                sx={{
                    mt: 3,
                    borderBottom: `1px solid ${BORDER}`,
                    '& .MuiTab-root': {
                        minWidth: 0,
                        minHeight: 44,
                        fontFamily: INTER,
                        fontWeight: 700,
                        fontSize: 12.5,
                        textTransform: 'none',
                    },
                }}
            >
                <Tab id="pronunciation-score-tab" aria-controls="pronunciation-score-panel" value="score" label="Your score" />
                <Tab
                    id="pronunciation-recordings-tab"
                    aria-controls="pronunciation-recordings-panel"
                    value="recordings"
                    label={`Your recordings (${recordings.length})`}
                />
            </Tabs>
            {loadError && (
                <Alert
                    severity="error"
                    sx={{ mt: 2 }}
                    action={
                        <Button onClick={() => void fetchRecordings()} disabled={busy}>
                            Retry
                        </Button>
                    }
                >
                    {loadError}
                </Alert>
            )}
            <Box role="tabpanel" id={`pronunciation-${tab}-panel`} aria-labelledby={`pronunciation-${tab}-tab`}>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress aria-label="Loading recordings" />
                    </Stack>
                ) : tab === 'score' ? (
                    scoring ? (
                        <Stack role="status" alignItems="center" spacing={2} sx={{ py: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ color: SLATE }}>Grading your recording. Your score will appear here.</Typography>
                        </Stack>
                    ) : selected ? (
                        <Box sx={{ pt: 2.5 }}>
                            {statusMessage ? (
                                <Alert
                                    severity="warning"
                                    action={
                                        <IconButton disabled={busy} aria-label="Play your recording" onClick={() => void play(selected)}>
                                            <VolumeUpRoundedIcon />
                                        </IconButton>
                                    }
                                >
                                    {statusMessage}
                                </Alert>
                            ) : (
                                <>
                                    <Stack
                                        direction={{ xs: 'column', lg: 'row' }}
                                        alignItems="center"
                                        spacing={2.5}
                                        sx={{ p: 2, borderRadius: '16px', backgroundColor: SURFACE_MUTED }}
                                    >
                                        <Box
                                            role="img"
                                            aria-label={scoreNumber === null ? 'Score unavailable' : `Score ${scoreNumber} out of 100`}
                                            sx={{ position: 'relative', width: 72, height: 72, flexShrink: 0 }}
                                        >
                                            <CircularProgress
                                                variant="determinate"
                                                value={100}
                                                size={72}
                                                thickness={4}
                                                sx={{ color: PROGRESS_TRACK, position: 'absolute' }}
                                            />
                                            <CircularProgress
                                                variant="determinate"
                                                value={Math.max(0, Math.min(100, scoreNumber ?? 0))}
                                                size={72}
                                                thickness={4}
                                                sx={{ color: BLUE, position: 'absolute', '& circle': { strokeLinecap: 'round' } }}
                                            />
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    display: 'grid',
                                                    placeItems: 'center',
                                                    color: NAVY,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {scoreNumber === null ? '—' : `${scoreNumber}%`}
                                            </Box>
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                                {selected.passing_grade === ScoreGrade.Good ? (
                                                    <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 19 }} />
                                                ) : (
                                                    <GraphicEqRoundedIcon sx={{ color: BLUE, fontSize: 19 }} />
                                                )}
                                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 14, color: NAVY }}>
                                                    {selected.passing_grade === ScoreGrade.Good ? 'Strong attempt' : 'Keep practicing'}
                                                </Typography>
                                            </Stack>
                                            <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: SLATE, mt: 0.4 }}>
                                                Review the sounds below and compare your recording with the model.
                                            </Typography>
                                        </Box>
                                        <Button
                                            disabled={busy}
                                            onClick={() => void play(selected)}
                                            startIcon={<VolumeUpRoundedIcon />}
                                            sx={{ fontSize: 12.5, fontWeight: 700, textTransform: 'none' }}
                                        >
                                            Play your recording
                                        </Button>
                                    </Stack>
                                    <ScoreDetails score={selected} />
                                </>
                            )}
                        </Box>
                    ) : (
                        !loadError && (
                            <Alert severity="info" sx={{ mt: 2.5 }}>
                                Record the sentence to see your score and pronunciation feedback.
                            </Alert>
                        )
                    )
                ) : (
                    <Stack spacing={1} sx={{ pt: 2 }}>
                        {checked.length > 0 && (
                            <Button
                                disabled={busy}
                                onClick={() => void deleteRecordings()}
                                startIcon={<DeleteOutlineRoundedIcon />}
                                sx={{ alignSelf: 'flex-end', color: ERROR_TEXT, textTransform: 'none' }}
                            >
                                Delete selected
                            </Button>
                        )}
                        {!recordings.length && !loadError && (
                            <Alert severity="info">You do not have any recordings for this sentence yet.</Alert>
                        )}
                        {recordings.map((attempt, index) => (
                            <Stack
                                key={attempt.id}
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ p: 1.25, borderRadius: '14px', backgroundColor: SURFACE_MUTED }}
                            >
                                <Checkbox
                                    disabled={busy}
                                    checked={checked.includes(attempt.id)}
                                    onChange={(event) =>
                                        setChecked((current) =>
                                            event.target.checked ? [...current, attempt.id] : current.filter((id) => id !== attempt.id),
                                        )
                                    }
                                    inputProps={{ 'aria-label': `Select recording: ${when(attempt, index)}` }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Button
                                        disabled={busy}
                                        onClick={() => {
                                            setSelectedId(attempt.id);
                                            setTab('score');
                                        }}
                                        sx={{
                                            p: 0,
                                            textAlign: 'left',
                                            textTransform: 'none',
                                            fontSize: 12.5,
                                            fontWeight: 700,
                                            color: NAVY,
                                        }}
                                    >
                                        {when(attempt, index)}
                                    </Button>
                                    <Typography sx={{ fontSize: 11.5, color: SLATE }}>
                                        {sentenceScoreStatusMessage(attempt) ? 'Try this sentence again' : 'Saved recording'}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 14, color: NAVY }}>
                                    {!sentenceScoreStatusMessage(attempt) && numeric(attempt.score_mean)
                                        ? `${Math.round(attempt.score_mean)}%`
                                        : '—'}
                                </Typography>
                                <IconButton
                                    disabled={busy}
                                    aria-label={`Play recording: ${when(attempt, index)}`}
                                    onClick={() => void play(attempt)}
                                    sx={{ color: BLUE }}
                                >
                                    <VolumeUpRoundedIcon />
                                </IconButton>
                            </Stack>
                        ))}
                    </Stack>
                )}
            </Box>
            <TeacherAudioReportDialog
                context={{
                    sentenceId: sentence.id,
                    textToSpeechSpeakerId: getSelectedTeacherSpeakerRole(),
                    audioText: sentence.text,
                    audioRole: 'pronunciation_sentence_playback',
                }}
                open={reportOpen}
                onClose={() => setReportOpen(false)}
            />
        </>
    );
}
